import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { STATES } from "@/lib/content";

const field = "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[var(--sun)] focus:bg-white/[0.07]";
const label = "block font-mono text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2";

const Chips = ({ name, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o} data-testid={`quote-form-${name}-${o.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onChange(o)} className={`rounded-full px-4 py-2 text-sm transition-colors ${value === o ? "bg-[var(--sun)] text-[var(--ink)]" : "bg-white/5 text-slate-200 hover:bg-white/10"}`}>
        {o}
      </button>
    ))}
  </div>
);

export const QuoteForm = () => {
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", business: "", email: "", phone: "", state: "", monthly_bill: params.get("bill") || "", sites: params.get("sites") || "1", ownership: "", contact_pref: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await http.post("/leads", { ...f, monthly_bill: f.monthly_bill ? Number(f.monthly_bill) : null, sites: Number(f.sites) || 1, source: params.get("bill") ? "calculator" : "contact" });
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStatus("idle");
    }
  };

  if (status === "done")
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="quote-form-success" className="rounded-2xl bg-[var(--surface)] border border-white/10 p-10 text-center">
        <CheckCircle2 className="mx-auto text-[var(--sun)]" size={56} />
        <h3 className="mt-6 font-display font-black uppercase text-4xl">Thanks, {f.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-slate-300">We've got your details. Mark or Steve will be in touch shortly to book your free assessment.</p>
      </motion.div>
    );

  return (
    <form onSubmit={submit} data-testid="quote-form" className="rounded-2xl bg-[var(--surface)] border border-white/10 p-6 sm:p-10 grid sm:grid-cols-2 gap-5">
      <div><label className={label} htmlFor="q-name">Your name *</label><input id="q-name" required data-testid="quote-form-input-name" className={field} value={f.name} onChange={set("name")} /></div>
      <div><label className={label} htmlFor="q-biz">Carwash / business</label><input id="q-biz" data-testid="quote-form-input-business" className={field} value={f.business} onChange={set("business")} /></div>
      <div><label className={label} htmlFor="q-email">Email *</label><input id="q-email" required type="email" data-testid="quote-form-input-email" className={field} value={f.email} onChange={set("email")} /></div>
      <div><label className={label} htmlFor="q-phone">Phone *</label><input id="q-phone" required type="tel" minLength={6} data-testid="quote-form-input-phone" className={field} value={f.phone} onChange={set("phone")} /></div>
      <div>
        <label className={label} htmlFor="q-state">State</label>
        <select id="q-state" data-testid="quote-form-select-state" className={`${field} appearance-none`} value={f.state} onChange={set("state")}>
          <option value="" className="bg-[var(--ink)]">Select…</option>
          {STATES.map((s) => <option key={s} value={s} className="bg-[var(--ink)]">{s}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label} htmlFor="q-bill">Monthly bill $</label><input id="q-bill" type="number" min={0} data-testid="quote-form-input-bill" className={field} value={f.monthly_bill} onChange={set("monthly_bill")} /></div>
        <div><label className={label} htmlFor="q-sites">Sites</label><input id="q-sites" type="number" min={1} data-testid="quote-form-input-sites" className={field} value={f.sites} onChange={set("sites")} /></div>
      </div>
      <div className="sm:col-span-2"><span className={label}>Your site is</span><Chips name="ownership" options={["Owned", "Leased", "Rented"]} value={f.ownership} onChange={set("ownership")} /></div>
      <div className="sm:col-span-2"><span className={label}>Let's talk via</span><Chips name="contact" options={["Coffee on site", "Zoom call", "Phone call"]} value={f.contact_pref} onChange={set("contact_pref")} /></div>
      <div className="sm:col-span-2"><label className={label} htmlFor="q-msg">Anything else?</label><textarea id="q-msg" rows={4} data-testid="quote-form-input-message" className={field} value={f.message} onChange={set("message")} placeholder="Existing solar, roof size, power bill issues…" /></div>
      {error && <p data-testid="quote-form-error" className="sm:col-span-2 text-red-400 text-sm">{error}</p>}
      <button type="submit" disabled={status === "loading"} data-testid="quote-form-submit-button" className="sm:col-span-2 group flex items-center justify-between rounded-full bg-[var(--sun)] text-[var(--ink)] px-7 py-5 font-display font-bold uppercase tracking-wider text-lg transition-colors hover:bg-[var(--sun-deep)] disabled:opacity-70">
        {status === "loading" ? "Sending…" : "Book my free assessment"}
        {status === "loading" ? <Loader2 className="animate-spin" /> : <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};
